import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import {
  doc, getDoc, updateDoc, serverTimestamp, collection, getDocs
} from 'firebase/firestore';
import { uploadToCloudinary, uploadCertificateToCloudinary } from '../../config/cloudinary';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../Shared/Sidebar';

export default function EditParent() {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    phone: '',
    email: '',
    region: '',
    district: '',
    ward: '',
    street: '',
    occupation: '',
    numberOfChildren: '',
    childrenEnrolled: '',
    storyBefore: '',
    storyJourney: '',
    storyNow: '',
    businessType: '',
    businessLocation: '',
    capitalAmount: '',
    raSession1: false,
    raSession2: false,
    microfinanceTraining: false,
    hasBirthCertificate: '',
    photoURL: '',
    certificateURL: '',
  });

  const [allStudents, setAllStudents] = useState([]);
  const [linkedChildren, setLinkedChildren] = useState([]);
  const [childSearch, setChildSearch] = useState('');

  useEffect(() => {
    fetchParent();
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'students'));
        setAllStudents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error(e); }
    })();
  }, [id]);

  function toggleChild(student) {
    setLinkedChildren(prev => {
      const exists = prev.find(c => c.id === student.id);
      if (exists) return prev.filter(c => c.id !== student.id);
      return [...prev, { id: student.id, name: `${student.firstName} ${student.lastName}` }];
    });
  }

  async function fetchParent() {
    try {
      const docRef = doc(db, 'parents', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setForm({
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          dateOfBirth: data.dateOfBirth || '',
          gender: data.gender || '',
          maritalStatus: data.maritalStatus || '',
          phone: data.phone || '',
          email: data.email || '',
          region: data.region || '',
          district: data.district || '',
          ward: data.ward || '',
          street: data.street || '',
          occupation: data.occupation || '',
          numberOfChildren: data.numberOfChildren || '',
          childrenEnrolled: data.childrenEnrolled || '',
          storyBefore: data.familyStory?.before || data.storyBefore || '',
          storyJourney: data.familyStory?.journey || data.storyJourney || '',
          storyNow: data.familyStory?.now || data.storyNow || '',
          businessType: data.microfinance?.businessType || data.businessType || '',
          businessLocation: data.microfinance?.businessLocation || data.businessLocation || '',
          capitalAmount: data.microfinance?.capitalAmount || data.capitalAmount || '',
          raSession1: data.sessions?.raSession1 || false,
          raSession2: data.sessions?.raSession2 || false,
          microfinanceTraining: data.sessions?.microfinanceTraining || false,
          hasBirthCertificate: data.hasBirthCertificate || '',
          photoURL: data.photoURL || '',
          certificateURL: data.certificateURL || '',
        });
        setLinkedChildren(data.linkedChildren || []);
        if (data.photoURL) setPhotoPreview(data.photoURL);
        if (data.certificateURL) {
          if (data.certificateURL.includes('.pdf')) {
            setCertificatePreview('pdf');
          } else {
            setCertificatePreview(data.certificateURL);
          }
        }
      } else {
        setError('Parent not found!');
      }
    } catch (err) {
      console.error(err);
      setError('Could not load parent data.');
    }
    setFetching(false);
  }

  function handleChange(e) {
    const value = e.target.type === 'checkbox'
      ? e.target.checked
      : e.target.value;
    setForm({ ...form, [e.target.name]: value });
  }

  function handlePhoto(e) {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  }

  function handleCertificate(e) {
    const file = e.target.files[0];
    if (file) {
      setCertificate(file);
      if (file.type.startsWith('image/')) {
        setCertificatePreview(URL.createObjectURL(file));
      } else {
        setCertificatePreview('pdf');
      }
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let photoURL = form.photoURL;
      let certificateURL = form.certificateURL;

      if (photo) {
        photoURL = await uploadToCloudinary(photo);
      }
      if (certificate) {
        certificateURL = await uploadCertificateToCloudinary(
          certificate
        );
      }

      await updateDoc(doc(db, 'parents', id), {
        ...form,
        photoURL,
        certificateURL,
        linkedChildren,
        familyStory: {
          before: form.storyBefore,
          journey: form.storyJourney,
          now: form.storyNow,
          lastUpdated: serverTimestamp(),
          updatedBy: currentUser.uid,
        },
        microfinance: {
          businessType: form.businessType,
          businessLocation: form.businessLocation,
          capitalAmount: form.capitalAmount,
        },
        sessions: {
          raSession1: form.raSession1,
          raSession2: form.raSession2,
          microfinanceTraining: form.microfinanceTraining,
        },
        updatedBy: currentUser.uid,
        updatedAt: serverTimestamp(),
      });

      setSuccess('Parent updated successfully!');
      setTimeout(() => navigate(-1), 2000);
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    }
    setLoading(false);
  }

  if (fetching) {
    return (
      <div style={styles.layout}>
        <Sidebar />
        <div style={styles.main}>
          <div style={styles.loading}>
            Loading parent data...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>
              Edit Parent
            </div>
            <div style={styles.pageSub}>
              {form.firstName} {form.lastName}
            </div>
          </div>
          <button
            style={styles.btnOutline}
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
        </div>

        {success && (
          <div style={styles.successMsg}>{success}</div>
        )}
        {error && (
          <div style={styles.errorMsg}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>

          {/* PHOTO */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>Parent Photo</div>
            <div style={styles.photoArea}>
              <div style={styles.photoPreview}>
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={styles.photoImg}
                  />
                ) : (
                  <span style={styles.photoPlaceholder}></span>
                )}
              </div>
              <div>
                <label style={styles.uploadBtn}>
                  Change Photo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhoto}
                    style={{ display: 'none' }}
                  />
                </label>
                <div style={styles.uploadHint}>
                  JPG or PNG · Max 5MB
                </div>
              </div>
            </div>
          </div>

          {/* PERSONAL INFO */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Personal Information
            </div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>First Name *</label>
                <input style={styles.formInput} name="firstName" value={form.firstName} onChange={handleChange} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Last Name *</label>
                <input style={styles.formInput} name="lastName" value={form.lastName} onChange={handleChange} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Date of Birth</label>
                <input style={styles.formInput} name="dateOfBirth" type="date" value={form.dateOfBirth} onChange={handleChange} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Gender *</label>
                <select style={styles.formInput} name="gender" value={form.gender} onChange={handleChange} required>
                  <option value="">Select gender</option>
                  <option>Male</option>
                  <option>Female</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Marital Status</label>
                <select style={styles.formInput} name="maritalStatus" value={form.maritalStatus} onChange={handleChange}>
                  <option value="">Select status</option>
                  <option>Married</option>
                  <option>Single</option>
                  <option>Widowed</option>
                  <option>Divorced</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Phone Number *</label>
                <input style={styles.formInput} name="phone" value={form.phone} onChange={handleChange} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Email</label>
                <input style={styles.formInput} name="email" type="email" value={form.email} onChange={handleChange} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Occupation</label>
                <input style={styles.formInput} name="occupation" value={form.occupation} onChange={handleChange} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Number of Children</label>
                <input style={styles.formInput} name="numberOfChildren" type="number" value={form.numberOfChildren} onChange={handleChange} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Children Enrolled</label>
                <input style={styles.formInput} name="childrenEnrolled" type="number" value={form.childrenEnrolled} onChange={handleChange} />
              </div>
            </div>
          </div>

          {/* LOCATION */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>Location</div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Region *</label>
                <input style={styles.formInput} name="region" value={form.region} onChange={handleChange} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>District *</label>
                <input style={styles.formInput} name="district" value={form.district} onChange={handleChange} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Ward</label>
                <input style={styles.formInput} name="ward" value={form.ward} onChange={handleChange} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Street</label>
                <input style={styles.formInput} name="street" value={form.street} onChange={handleChange} />
              </div>
            </div>
          </div>

          {/* FAMILY STORY */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>Family Story</div>

            <div style={styles.storyBlock('before')}>
              <div style={styles.storyHeader}>
                <span style={styles.storyIcon}></span>
                <div style={styles.storyLabel}>Before We Met Them</div>
              </div>
              <textarea style={styles.storyInput} name="storyBefore" value={form.storyBefore} onChange={handleChange} rows={4} />
            </div>

            <div style={styles.storyBlock('journey')}>
              <div style={styles.storyHeader}>
                <span style={styles.storyIcon}></span>
                <div style={styles.storyLabel}>Their Journey With Us</div>
              </div>
              <textarea style={styles.storyInput} name="storyJourney" value={form.storyJourney} onChange={handleChange} rows={4} />
            </div>

            <div style={styles.storyBlock('after')}>
              <div style={styles.storyHeader}>
                <span style={styles.storyIcon}></span>
                <div style={styles.storyLabel}>Where They Are Now</div>
              </div>
              <textarea style={styles.storyInput} name="storyNow" value={form.storyNow} onChange={handleChange} rows={4} />
            </div>
          </div>

          {/* LINKED CHILDREN */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>Children Supported by Iyanna Foundation</div>
            <div style={styles.uploadHint}>Link this parent to their registered child/children.</div>
            <input
              style={{ ...styles.formInput, marginTop: '10px' }}
              placeholder="Search a registered student by name..."
              value={childSearch}
              onChange={e => setChildSearch(e.target.value)}
            />
            {childSearch && (
              <div style={styles.childResults}>
                {allStudents
                  .filter(st => `${st.firstName} ${st.lastName}`.toLowerCase().includes(childSearch.toLowerCase()))
                  .slice(0, 8)
                  .map(st => {
                    const isLinked = linkedChildren.some(c => c.id === st.id);
                    return (
                      <button
                        type="button"
                        key={st.id}
                        style={{ ...styles.childResultRow, ...(isLinked ? styles.childResultRowActive : {}) }}
                        onClick={() => toggleChild(st)}
                      >
                        {isLinked ? '✓ ' : ''}{st.firstName} {st.lastName} — {st.class || st.school || 'Student'}
                      </button>
                    );
                  })}
              </div>
            )}
            {linkedChildren.length > 0 && (
              <div style={styles.childChipRow}>
                {linkedChildren.map(c => (
                  <span key={c.id} style={styles.childChip}>
                    {c.name}
                    <button
                      type="button"
                      style={styles.childChipRemove}
                      onClick={() => setLinkedChildren(prev => prev.filter(x => x.id !== c.id))}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* BIRTH CERTIFICATE */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>Birth Certificate</div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Certificate Status</label>
                <select style={styles.formInput} name="hasBirthCertificate" value={form.hasBirthCertificate} onChange={handleChange}>
                  <option value="">Select option</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="processing">In process</option>
                </select>
              </div>
            </div>
            {form.hasBirthCertificate === 'yes' && (
              <div style={{ marginTop: '16px' }}>
                <label style={styles.uploadBtn}>
                  Change Certificate
                  <input type="file" accept="image/*,.pdf" onChange={handleCertificate} style={{ display: 'none' }} />
                </label>
                {certificatePreview && (
                  <div style={{ marginTop: '12px' }}>
                    {certificatePreview === 'pdf' ? (
                      <div style={styles.pdfPreview}>Certificate uploaded </div>
                    ) : (
                      <img src={certificatePreview} alt="Certificate" style={styles.certImg} />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* MICROFINANCE */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>Microfinance</div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Business Type</label>
                <input style={styles.formInput} name="businessType" value={form.businessType} onChange={handleChange} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Business Location</label>
                <input style={styles.formInput} name="businessLocation" value={form.businessLocation} onChange={handleChange} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Capital Amount (TSh)</label>
                <input style={styles.formInput} name="capitalAmount" type="number" value={form.capitalAmount} onChange={handleChange} />
              </div>
            </div>
            <div style={{ marginTop: '16px' }}>
              <div style={styles.sectionTitle}>Sessions Attended</div>
              {[
                { name: 'raSession1', label: 'RA Session 1' },
                { name: 'raSession2', label: 'RA Session 2' },
                { name: 'microfinanceTraining', label: 'Microfinance Training' },
              ].map((s) => (
                <label key={s.name} style={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    name={s.name}
                    checked={form[s.name]}
                    onChange={handleChange}
                    style={styles.checkbox}
                  />
                  <span style={styles.checkboxLabel}>{s.label}</span>
                </label>
              ))}
            </div>
          </div>

          <button
            type="submit"
            style={styles.submitBtn}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>

        </form>
      </div>
    </div>
  );
}

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#F6F4EE' },
  main: { flex: 1, padding: '26px', overflowY: 'auto' },
  loading: { textAlign: 'center', padding: '60px', fontSize: '16px', color: '#6B7280' },
  pageHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: '24px',
    flexWrap: 'wrap', gap: '14px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif", fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  pageSub: { fontSize: '13px', color: '#6B7280', marginTop: '3px' },
  btnOutline: {
    background: 'transparent', color: '#14452F',
    border: '1.5px solid #1D5C3C', padding: '10px 20px',
    borderRadius: '10px', fontFamily: 'inherit',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  successMsg: {
    background: '#EAF3EC', border: '1px solid #BBD8C4',
    color: '#14452F', padding: '12px 16px',
    borderRadius: '10px', fontSize: '13px', marginBottom: '20px',
  },
  errorMsg: {
    background: '#FFEBEE', border: '1px solid #FFCDD2',
    color: '#C62828', padding: '12px 16px',
    borderRadius: '10px', fontSize: '13px', marginBottom: '20px',
  },
  panel: {
    background: '#fff', borderRadius: '16px', padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '11px', letterSpacing: '0.8px',
    textTransform: 'uppercase', color: '#6B7280',
    marginBottom: '14px', paddingBottom: '8px',
    borderBottom: '1px solid #F3F4F6', fontWeight: '600',
  },
  photoArea: { display: 'flex', alignItems: 'center', gap: '20px' },
  photoPreview: {
    width: '80px', height: '80px', borderRadius: '50%',
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', flexShrink: 0,
  },
  photoImg: { width: '100%', height: '100%', objectFit: 'cover' },
  photoPlaceholder: { fontSize: '32px' },
  uploadBtn: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    color: '#fff', padding: '9px 18px', borderRadius: '9px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  uploadHint: { fontSize: '11.5px', color: '#6B7280', marginTop: '6px' },
  childResults: { marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '200px', overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: '9px', padding: '6px' },
  childResultRow: { textAlign: 'left', background: 'transparent', border: 'none', padding: '9px 10px', borderRadius: '7px', fontSize: '13px', color: '#1C1C1E', cursor: 'pointer', fontFamily: 'inherit' },
  childResultRowActive: { background: 'rgba(20,69,47,0.08)', color: '#14452F', fontWeight: '600' },
  childChipRow: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' },
  childChip: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(20,69,47,0.08)', color: '#14452F', fontSize: '12.5px', fontWeight: '600', padding: '6px 8px 6px 12px', borderRadius: '20px' },
  childChipRemove: { background: 'rgba(20,69,47,0.15)', border: 'none', color: '#14452F', width: '18px', height: '18px', borderRadius: '50%', fontSize: '13px', lineHeight: 1, cursor: 'pointer' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
  formLabel: { fontSize: '12px', color: '#6B7280', fontWeight: '500' },
  formInput: {
    padding: '11px 14px', borderRadius: '9px',
    border: '1.5px solid #E5E7EB', background: '#FAF9F5',
    fontFamily: 'inherit', fontSize: '13.5px',
    color: '#1C1C1E', outline: 'none',
  },
  storyBlock: (type) => ({
    borderRadius: '12px', padding: '16px', marginBottom: '12px',
    background: type === 'before' ? '#FBF8F0' : type === 'journey' ? '#f1f8f1' : '#e8f5e9',
    border: type === 'before' ? '1px solid #FFE0B2' : type === 'journey' ? '1px solid #c8e6c9' : '1px solid #a5d6a7',
  }),
  storyHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  storyIcon: { fontSize: '22px' },
  storyLabel: { fontSize: '13px', fontWeight: '600', color: '#1C1C1E' },
  storyInput: {
    width: '100%', padding: '11px 14px', borderRadius: '9px',
    border: '1.5px solid rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.7)',
    fontFamily: 'inherit', fontSize: '13.5px', color: '#1C1C1E',
    outline: 'none', resize: 'vertical',
  },
  pdfPreview: {
    background: '#EAF3EC', border: '1px solid #BBD8C4',
    color: '#14452F', padding: '12px 16px', borderRadius: '10px', fontSize: '13px',
  },
  certImg: { width: '100%', maxWidth: '300px', borderRadius: '10px', border: '1px solid #E5E7EB' },
  checkboxRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '10px 0', borderBottom: '1px solid #F3F4F6', cursor: 'pointer',
  },
  checkbox: { width: '16px', height: '16px', accentColor: '#14452F' },
  checkboxLabel: { fontSize: '13px', color: '#1C1C1E' },
  submitBtn: {
    width: '100%', padding: '15px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff', border: 'none', borderRadius: '12px',
    fontFamily: 'inherit', fontSize: '15px', fontWeight: '600',
    cursor: 'pointer', boxShadow: '0 6px 20px rgba(20,69,47,0.3)',
    marginBottom: '30px',
  },
};